import * as React from "react";
import * as Redux from "redux";
import * as ReactRedux from "react-redux";

import * as Mousetrap from "mousetrap";
import * as R from "ramda";
import classNames from "classnames";

import store from "../../store/store";

import DocMediaModal from "./MediaModal";
import DocEditMetadataModal from "./EditMetadataModal";
import DocSection from "./Section";
import DocModals from "./Modals";
import DocTopBar from "./TopBar";

import GenLoadingSpinner from "../Gen/LoadingSpinner";

import {
  addActivitiesToDocument,
  filterActivitiesBySchedule,
  combineActivities,
  activityContentToSection,
} from "../../src/activities";

import {
  inlineHTMLToJSON,
} from "../../src/convert";

import {
  DisplayMode,
  displayTraitsBasedOnMode,
} from "../../src/display_mode";

import {
  deepFindByUID,
  deepFindLineageByUID,
  generateNode,
  newSetNodeKVPairAction,
  addCloseDrawersButtonToContent,
} from "../../src/doc_helpers";

import {
  hideUntilShouldHideNow
} from "../../src/effective_dates";

import {
  getDocRangeSelection,
  domRangeToDocRange,
} from "../../src/selection";

import {
  nowInSeconds,
} from "../../src/now_in_seconds";

import {
  applyState,
} from "../../src/state";

import {
  documentSetDocumentStateAndActionsFromStore,
} from "../../reducers/document_set_document_state";

import {
  documentAndActionsFromStore,
} from "../../reducers/document";

import {
  documentSetDocumentRelationAndActionsFromStore,
} from "../../reducers/document_set_document_relation";

import {
  versionAndActionsFromStore,
} from "../../reducers/version";

import {
  activityAndActionsFromStore,
  activityListAndActionsFromStore,
} from "../../reducers/activity";

import {
  activityUserStateAndActionsFromStore,
} from "../../reducers/activity_user_state";

import {
  documentActivityAmendmentListAndActionsFromStore,
} from "../../reducers/document_activity_amendment";

import {
  documentActivityListAndActionsFromStore,
} from "../../reducers/document_activity";

import {
  documentSetDocumentActivityListAndActionsFromStore,
} from "../../reducers/document_set_document_activity";

type OwnProps = OwnPropsActivity | OwnPropsDocument;

interface OwnPropsBase {
  isEditor?: boolean;
  documentID: number;

  currentUser: UserSnakeModel;
  currentRoleDefinition: RoleDefinitionSnakeModel;
  currentGroup: GroupSnakeModel;

  documentSetID?: number;
  documentSetDocumentRelationID?: number;
  isGated?: boolean;

  nextDocumentSetDocumentRelationId?: number;

  imagingTypes: Array<ImagingType>;
}

interface OwnPropsActivity extends OwnPropsBase {
  type: "Activity";
}

interface OwnPropsDocument extends OwnPropsBase {
  type: "Document";
  versionID: number;
}

const mapStateToProps = (state: GlobalStore, oProps: OwnProps) => {
  return state;
};

const mapDispatchToProps = (dispatch: Redux.Dispatch, oProps: OwnProps) => {
  return {
    dispatch: dispatch,
  };
};

const versionLocalActions = (currentVersion: CurrentVersion) => {
  return {
    setCurrentlyEditing: (currentlyEditing: CurrentlyEditing | undefined) => currentVersion._actions.setLocalFields({currentlyEditing}),
    setDisplayMode: (displayMode: DisplayMode) => currentVersion._actions.setLocalFields({displayMode}),
    setOpenMenu: (openMenu?: Menu) => currentVersion._actions.setLocalFields({openMenu}),
    setEditMetadata: (editMetadataUid: string | undefined) => currentVersion._actions.setLocalFields({editMetadataUid}),
  };
};

const mergeProps = (state: ReturnType<typeof mapStateToProps>, dispatch: ReturnType<typeof mapDispatchToProps>, oProps: OwnProps) => {
  const {
    type,
    documentID,
    documentSetID,
    documentSetDocumentRelationID,
    isGated,
    currentUser,
    currentRoleDefinition,
  } = oProps;

  if (type === "Activity") {
    const activityDocument = activityAndActionsFromStore(dispatch.dispatch, state)(documentID);

    return {
      ...oProps,
      currentDocument: activityDocument && {...activityDocument, type: "Activity" as const},
      currentVersion: activityDocument,
      ...versionLocalActions(activityDocument),

      possibleActivities: [], // TODO: REMOVE
      activityUserStatesByActivityId: {}, // TODO: REMOVE
      currentDocumentSetDocumentRelation: undefined, // TODO: REMOVE
      activityActions: undefined, // TODO: REMOVE
    };
  } else {
    const {
      versionID,
    } = oProps;

    const addableActivities = activityListAndActionsFromStore(dispatch.dispatch, state)("addable_activities", "activities");

    const currentDocumentActivities = documentActivityListAndActionsFromStore(dispatch.dispatch, state)("currentDocumentActivities", `documents/${documentID}/document_activities`);
    const currentDocumentSetDocumentActivities = documentSetDocumentActivityListAndActionsFromStore(dispatch.dispatch, state)("currentDocumentSetDocumentActivities", `document_set_document_relations/${documentSetDocumentRelationID}/document_set_document_activities`);
    const currentDocumentActivityAmendments = documentActivityAmendmentListAndActionsFromStore(dispatch.dispatch, state)("currentDocumentActivityAmendments", `document_set_document_relations/${documentSetDocumentRelationID}/document_activity_amendments`);

    const possibleActivities = combineActivities(
      "listData" in currentDocumentSetDocumentActivities ? currentDocumentSetDocumentActivities.listData : [],
      "listData" in currentDocumentActivities ? currentDocumentActivities.listData : [],
      "listData" in currentDocumentActivityAmendments ? currentDocumentActivityAmendments?.listData : [],
      "listData" in addableActivities ? addableActivities?.listData : [],
    );

    const currentActivities = filterActivitiesBySchedule(possibleActivities);

    const activityActions = {
      currentDocumentSetDocumentActivitiesActions: currentDocumentSetDocumentActivities._actions,
      currentDocumentActivitiesActions: currentDocumentActivities._actions,
      currentDocumentActivityAmendmentsActions: currentDocumentActivityAmendments._actions,
      addableActivitiesActions: addableActivities._actions,
    };

    const documentSetDocumentState = isGated && documentSetID ? {documentSetDocumentState: documentSetDocumentStateAndActionsFromStore(dispatch.dispatch, state)(`${documentSetID}/${documentID}`)} : {};
    const currentVersion = versionAndActionsFromStore(dispatch.dispatch, state)(versionID);

    const currentDocumentSetDocumentRelation = documentSetDocumentRelationID ? {currentDocumentSetDocumentRelation: documentSetDocumentRelationAndActionsFromStore(dispatch.dispatch, state)(documentSetDocumentRelationID)} : {currentDocumentSetDocumentRelation: undefined};

    return {
      ...oProps,
      currentDocument: documentAndActionsFromStore(dispatch.dispatch, state)(documentID),
      currentVersion: currentVersion,
      ...versionLocalActions(currentVersion),

      ...documentSetDocumentState,

      ...currentDocumentSetDocumentRelation,

      activityUserStatesByActivityId: R.fromPairs(possibleActivities.map((activity) => {
        const activityId = activity.id;
        const longActivityId = documentSetID ? `${activityId}/${documentSetID}/${documentID}` : `${activityId}/${documentID}`;
        return [activityId, activityUserStateAndActionsFromStore(dispatch.dispatch, state)(longActivityId)];
      })),
      currentActivities,
      activityActions,
      possibleActivities,
      addableActivities,
      currentDocumentActivities,
      currentDocumentSetDocumentActivities,
      currentDocumentActivityAmendments,
    };
  }
};

type DocContainerProps = ReturnType<typeof mergeProps>

interface DocContainerState {
  syncCounter: number;
  uscData: Array<Usc>;
}

class DocContainer extends React.Component<DocContainerProps, DocContainerState> {
  state = {
    syncCounter: 0,
    uscData: [] as Array<Usc>,
  };

  private getUSCSTimer?: number;

  componentDidMount() {
    const {
      // currentActivities=[],
      documentSetDocumentRelationID,
      currentDocument,
      currentDocumentSetDocumentRelation,
      currentRoleDefinition,
      currentVersion,
      isEditor,
      isGated,

      documentID,
    } = this.props;

    currentVersion._actions.resetUndoRedo();
    currentVersion._actions.read();
    currentDocument._actions.read();

    if (currentDocumentSetDocumentRelation) {
      currentDocumentSetDocumentRelation._actions.read();
    }

    // let stateObj = { section_uid: topSectionUID };
    // for (let attrname in history.state) { stateObj[attrname] = history.state[attrname]; }
    // history.replaceState(stateObj, "", "?section_uid=" + topSectionUID);
    //
    // window.onpopstate = (event) => {
    //   if (event.state) {
    //     if (event.state.section_uid) {
    //       this.setState({displayMode: "normal"});
    //     }
    //   }
    // };

    if ("documentSetDocumentState" in this.props && this.props.documentSetDocumentState) { // TODO: Need after the &&?
      this.props.documentSetDocumentState._actions.read();
    } else {
      this.getUSCS();
      this.getUSCSTimer = setInterval(this.getUSCS.bind(this), 60000);
    }

    if (isEditor) {
      document.addEventListener("selectionchange", this.onSelectionChange.bind(this), false);
      document.addEventListener("beforeinput", this.handleEventListener.bind(this), false);

      Mousetrap.bind("command+s", (e) => {e.preventDefault(); this.save();});
    } else {
      this.logEvent("root", "section_opened");

      if ("addableActivities" in this.props) {
        const {
          addableActivities,
          currentDocumentActivities,
          currentDocumentActivityAmendments,
          currentDocumentSetDocumentActivities,
        } = this.props;

        if (!(currentRoleDefinition.name === "Learner" || isGated || !currentDocumentSetDocumentRelation)) {
          addableActivities._actions.readPage();
        }

        if (documentID) {
          currentDocumentActivities._actions.readPage();
        }

        if (documentSetDocumentRelationID) {
          currentDocumentActivityAmendments._actions.readPage();
          currentDocumentSetDocumentActivities._actions.readPage();
        }
      }
    }
  }

  componentDidUpdate(prevProps: DocContainerProps) {
    if (this.props.type !== "Activity") {
      const {
        possibleActivities,
        activityUserStatesByActivityId,
      } = this.props;

      const prevActivityIds = ("possibleActivities" in prevProps) ? prevProps.possibleActivities?.map((activity) => activity.id) : [];
      const currentActivityIds = possibleActivities.map((activity) => activity.id);
      const newActivityIds = R.difference(currentActivityIds, prevActivityIds);

      if (newActivityIds.length > 0) {
        newActivityIds.forEach((id) => activityUserStatesByActivityId[id]._actions.read());
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener("selectionchange", this.onSelectionChange.bind(this), false);
    document.removeEventListener("beforeinput", this.handleEventListener.bind(this), false);

    clearInterval(this.getUSCSTimer);

    Mousetrap.unbind("command+s");
  }

  onSelectionChange() {
    const {
      currentVersion,
      setCurrentlyEditing,
    } = this.props;

    const {
      currentlyEditing,
    } = currentVersion._localData;

    const newDocSelection = getDocRangeSelection();

    if (newDocSelection && newDocSelection.uid) {
      if (!currentlyEditing || !R.equals(R.pick(["uid", "key", "range"], currentlyEditing), newDocSelection)) {
        setCurrentlyEditing(newDocSelection);
      }
    } else if (currentlyEditing) {
      // setCurrentlyEditing(R.omit(['contentKey', 'range'], currentlyEditing))
    }
  }

  openSection(uid: string) {
    // TODO: REVISE OPEN SECTION
    // this.setState({displayMode: "normal", displayRootSectionUID: uid});
    // let stateObj = { section_uid: uid };
    // history.pushState(stateObj, "", "?section_uid=" + uid);
    this.logEvent(uid, "section_opened");
  }

  save() {
    const {
      currentVersion,
      type,
    } = this.props;

    if (type === "Activity" && !confirm("Are you sure you want to save? Changes to the activity will push out to all users using this activity immediately.")) {
      return;
    }

    currentVersion._actions.update();
  }

  logEvent(node_uid: string, event: LogEventName, data?: string) {
    const logEvent: LogEvent = {"node_uid": node_uid, "event": event, "created_at": nowInSeconds()};

    if (data !== "undefined") {
      logEvent.data = data;
    }

    this.postDocumentUserLogEvents([logEvent]);
  }

  postDocumentUserLogEvents(events: Array<LogEvent>) {
    const {
      type,
      currentUser,
      documentID,
    } = this.props;

    if (type === "Activity") { return; }

    if (currentUser) {
      fetch(`/documents/${documentID}/document_user_logs/create_batch`, {
        method: "POST",
        body: JSON.stringify({"events": events}),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) { throw new Error(`${response.status} response`); }

          return response.json();
        })
        .then(() => {})
        .catch(() => {});
    }
  }

  getUSCS() {
    const {
      currentUser,
      documentID,
    } = this.props;

    const {
      syncCounter,
      uscData,
    } = this.state;

    if (currentUser && documentID) {
      fetch(`/documents/${documentID}/usc?lastSyncCounter=${syncCounter}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) { throw new Error(`${response.status} response`); }

          return response.json();
        })
        .then((data) => this.setState({uscData: uscData.concat(data.usc), syncCounter: data.sync_counter}))
        .catch(() => {});
    }
  }

  syncUSCS(uscs: Array<Usc>) {
    const {
      currentUser,
      documentID,
    } = this.props;

    const {
      syncCounter,
      uscData,
    } = this.state;

    if (currentUser) {
      fetch(`/documents/${documentID}/usc`, {
        method: "POST",
        body: JSON.stringify({"usc": uscs, "lastSyncCounter": syncCounter}),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) { throw new Error(`${response.status} response`); }

          return response.json();
        })
        .then((data) => {
          this.setState({uscData: uscData.concat(data.usc), syncCounter: data.sync_counter});
        })
        .catch(() => {});
    } else {
      this.setState({uscData: uscData.concat(uscs), syncCounter: 0});
    }
  }

  addDocumentUserState(state: StrippedState) {
    const uscs = R.flatten(
      R.map(([uid, kvObject]) => {
        return R.map(([key, value]) => {
          return {
            uid: uid,
            [key]: value,
            timeStamp: Date.now(),
          };
        }, R.toPairs(kvObject));
      }, R.toPairs(state)),
    );

    this.syncUSCS(uscs);
  }

  resetDocumentUserState() {

  }

  mergeInUSCS<T extends PossibleContent>(json: T, uscs: Array<Usc>): T {
    const clonedVersion = R.clone(json);

    for (const usc of uscs) {
      const node = deepFindByUID(clonedVersion, usc.uid);
      if (node) {
        for (const prop in usc) {
          if (prop !== "uid" && prop !== "timeStamp") {
            (node as any)[prop] = usc[prop];
          }
        }
      }
    }

    return clonedVersion;
  }

  handleEventListener(inputEvent: InputEvent) {
    const {
      currentVersion,
    } = this.props;

    const {
      currentlyEditing,
    } = currentVersion._localData;

    const {
      undo,
      redo,
      handleTextInsert,
      handleDataTransferInsert,
      handleNewLineInsert,
      handleDelete,
      handleStyle,
    } = currentVersion._actions;

    switch (inputEvent.inputType) {
      case "historyUndo":
        inputEvent.preventDefault();
        return undo();
      case "historyRedo":
        inputEvent.preventDefault();
        return redo();
    }

    const targetRanges = inputEvent.getTargetRanges();
    const firstStaticRange = targetRanges[0];

    const selectionDocRange = domRangeToDocRange(firstStaticRange);
    if (!selectionDocRange) { return; }

    inputEvent.preventDefault();

    const combinedSelection = {...(currentlyEditing || {}), ...selectionDocRange};

    switch (inputEvent.inputType) {
      case "insertText":
        if (!inputEvent.data) { return; }

        return handleTextInsert(inputEvent.data, combinedSelection);
      case "insertReplacementText":
      case "insertFromYank":
      case "insertFromDrop":
      case "insertFromPaste":
      case "insertFromPasteAsQuotation":
      case "insertTranspose":
        if (!inputEvent.dataTransfer) { return; }

        return handleDataTransferInsert(inputEvent.dataTransfer, combinedSelection);
      case "insertLineBreak":
      case "insertParagraph":
        return handleNewLineInsert(combinedSelection);
      case "deleteWordBackward":
      case "deleteWordForward":
      case "deleteSoftLineBackward":
      case "deleteSoftLineForward":
      case "deleteEntireSoftLine":
      case "deleteHardLineBackward":
      case "deleteHardLineForward":
      case "deleteByDrag":
      case "deleteByCut":
      case "deleteContent":
      case "deleteContentBackward":
      case "deleteContentForward":
        return handleDelete(combinedSelection);
      case "formatBold":
        return handleStyle("b", combinedSelection);
      case "formatItalic":
        return handleStyle("i", combinedSelection);
      case "formatUnderline":
        return handleStyle("u", combinedSelection);
      case "formatStrikeThrough":
      case "formatSuperscript":
        return handleStyle("sup", combinedSelection);
      case "formatSubscript":
        return handleStyle("sub", combinedSelection);
      case "insertLink":
        // TODO: ADD ABILITY
      case "formatRemove":
        // TODO: REMOVE FORMATTING
      case "insertOrderedList":
      case "insertUnorderedList":
      case "insertHorizontalRule":
      case "insertCompositionText":
      case "formatJustifyFull":
      case "formatJustifyCenter":
      case "formatJustifyRight":
      case "formatJustifyLeft":
      case "formatIndent":
      case "formatOutdent":
      case "formatSetBlockTextDirection":
      case "formatSetInlineTextDirection":
      case "formatBackColor":
      case "formatFontColor":
      case "formatFontName":
      default:
        return;
    }
  }

  render() {
    if (!("lastSyncedAt" in this.props.currentDocument) || !("lastSyncedAt" in this.props.currentVersion) || !(!this.props.currentDocumentSetDocumentRelation || "lastSyncedAt" in this.props.currentDocumentSetDocumentRelation)) { return <GenLoadingSpinner/>; }

    const {
      type,
      activityUserStatesByActivityId,
      currentDocument,
      isGated,
      currentDocumentSetDocumentRelation,
      currentGroup,
      currentRoleDefinition,
      currentVersion,
      isEditor,
      imagingTypes,
      setDisplayMode,
      setOpenMenu,
      possibleActivities,
      setCurrentlyEditing,
      setEditMetadata,
      activityActions,
    } = this.props;

    const addDocumentUserState = "documentSetDocumentState" in this.props && this.props.documentSetDocumentState ? this.props.documentSetDocumentState._actions.addState : this.addDocumentUserState.bind(this),

    // CURRENT USER
    currentUser = {
      ...this.props.currentUser,
      addDocumentUserState: addDocumentUserState,
      activityUserStatesByActivityId: activityUserStatesByActivityId,
      addState: addDocumentUserState,
      addStateTargetType: "document" as const,
      resetDocumentSetDocumentState: "documentSetDocumentState" in this.props && this.props.documentSetDocumentState ? this.props.documentSetDocumentState._actions.resetState : this.resetDocumentUserState.bind(this),
      logEvent: this.logEvent.bind(this),
    };

    const currentRoleDefinitionCompiled = {
      ...currentRoleDefinition,
      isLearner: currentRoleDefinition.name === "Learner",
    };

    // DISPLAY STATE
    const displayMode = currentVersion._localData.displayMode || (isEditor ? DisplayMode.EDIT : DisplayMode.STUDENT);
    const {
      isUnlocked,
      isForcedUncollapsed,
      isEditDisplayMode,
    } = displayTraitsBasedOnMode(displayMode);

    const displayState = {
      displayMode,
      isUnlocked,
      isForcedUncollapsed,
      setDisplayMode: setDisplayMode,
      openSection: this.openSection.bind(this),
      documentType: type,
      openMenu: currentVersion._localData.openMenu,
      setOpenMenu,
    };

    // EDIT STATE
    let editState: EditState = {
      isEditor: false as const,
    };
    if (isEditor) {
      const content = "json" in currentVersion ?  currentVersion.json : currentVersion.content;

      editState = {
        isEditor: true,
        isEditing: isEditDisplayMode,
        hasUnsavedChanges: !R.isEmpty(currentVersion._editData),
        setEditFieldsVersion: currentVersion._actions.setEditFields,
        undoPossible: (currentVersion._localData.undoQueue || []).length > 0,
        redoPossible: (currentVersion._localData.redoQueue || []).length > 0,
        save: this.save.bind(this),
        undo: currentVersion._actions.undo,
        redo: currentVersion._actions.redo,
        currentlyEditing: currentVersion._localData.currentlyEditing,
        setCurrentlyEditing: setCurrentlyEditing,
        setEditMetadata: setEditMetadata,
        editMetadataUid: currentVersion._localData.editMetadataUid,
        currentlyActiveStyles: [],
      };

      if (editState.currentlyEditing && editState.currentlyEditing.uid) {
        const lineage = deepFindLineageByUID(content, editState.currentlyEditing.uid);

        if (lineage) {
          editState.currentlyEditingAncestors = R.init(lineage);
          editState.currentlyEditingNode = R.last(lineage);

          const editingRange = editState.currentlyEditing.range;
          if (editState.currentlyEditing.key && editingRange && editState.currentlyEditingNode) {
            const currentlyEditingInlineJSON = inlineHTMLToJSON((editState.currentlyEditingNode as any)[editState.currentlyEditing.key]);

            editState.currentlyActiveStyles = currentlyEditingInlineJSON.styles.filter((style) => style.range.start <= editingRange.start && style.range.end >= editingRange.end);
          }
        }
      }
    }

    ////////////////////////
    ////////////////////////

    // CONVERT ACTIVITY TO SECTION
    let rootNode: SectionNode;
    let progressBar: React.ReactNode;
    let caseReview: React.ReactNode;
    if (this.props.type === "Activity") {
      const {
        currentDocument,
        currentVersion,
      } = this.props;

      rootNode = activityContentToSection(R.clone(currentVersion.content), currentDocument.name)
    } else {
      const {
        activityUserStatesByActivityId,
        possibleActivities,
        currentVersion,
      } = this.props;

      let {
        currentActivities,
      } = this.props;

      // APPLY STATE TO DOCUMENT CONTENT
      rootNode = R.clone(currentVersion.json);
      if ("documentSetDocumentState" in this.props && this.props.documentSetDocumentState && "lastSyncedAt" in this.props.documentSetDocumentState) {
        rootNode = applyState(rootNode, this.props.documentSetDocumentState?.state || {});
      } else {
        rootNode = this.mergeInUSCS(rootNode, this.state.uscData);
      }

      // ADD ACTIVITIES TO DOCUMENT
      // TODO: Change to all possible Activities
      if (!R.isNil(currentActivities)) {
        currentActivities = currentRoleDefinitionCompiled.isLearner || isGated || !currentDocumentSetDocumentRelation ? currentActivities : possibleActivities;

        if ("documentSetDocumentState" in this.props && this.props.documentSetDocumentState && "lastSyncedAt" in this.props.documentSetDocumentState) {
          const documentSetDocumentState = this.props.documentSetDocumentState; // TODO: FIX

          currentActivities = currentActivities.filter((activity) => !("document_set_document_activity" in activity) || activity.document_set_document_activity?.attempt_index === documentSetDocumentState.attempt_index);
        }
        rootNode = addActivitiesToDocument(rootNode, currentActivities, activityUserStatesByActivityId);
      }

      if (currentDocumentSetDocumentRelation && (currentRoleDefinitionCompiled.isLearner || isGated)) {
        rootNode.content = rootNode.content.filter((node) => {
          const shouldHide =
            node.type === "section" &&
            currentDocumentSetDocumentRelation &&
            currentDocumentSetDocumentRelation.drawer_schedules[node.uid] &&
            hideUntilShouldHideNow(currentDocumentSetDocumentRelation.drawer_schedules[node.uid].hide_until)

          return !shouldHide
        })
      }

      // FLOW DOCUMENT
      if (currentRoleDefinitionCompiled.isLearner) {
        const firstVideo = rootNode.content.find((node) => "video" === node.type);
        const firstVideoNotPlayed = !!firstVideo && !firstVideo.video_played

        if (isGated && firstVideoNotPlayed) {
          firstVideo.onVideoComplete = [
            newSetNodeKVPairAction(firstVideo.uid, "video_played", true),
          ];
          firstVideo.extraTitle = "Watch the video to continue.";

          const firstNode = rootNode.content.find((node) => ["section", "activity"].includes(node.type));
          if (firstNode) {
            const firstNodeAction = firstNode.type === "activity" ?
              newSetNodeKVPairAction("root", "collapsed", false, "activity", firstNode.id) :
              newSetNodeKVPairAction(firstNode.uid, "collapsed", false, "document");

            firstVideo.onVideoComplete = [
              ...firstVideo.onVideoComplete,
              firstNodeAction,
            ];
          }
        }

        const reduceResult = rootNode.content.reduce<{shouldHide: boolean, newContent: Array<BlockContent>}>((accum, node, currentIndex, array) => {
          const {
            shouldHide,
            newContent,
          } = accum;

          if (shouldHide || (isGated && firstVideoNotPlayed)) {
            if (["section", "activity"].includes(node.type)) {
              if (isGated) {
                return {
                  shouldHide: shouldHide,
                  newContent: [
                    ...newContent,
                    {
                      ...node,
                      inactive: 2,
                    },
                  ],
                };
              } else {
                return {
                  shouldHide: shouldHide,
                  newContent: [
                    ...newContent,
                    {
                      ...node,
                      invisible: true,
                    },
                  ],
                };
              }
            } else {
              return {
                shouldHide,
                newContent: [...newContent, node],
              };
            }
          } else {
            let shouldHideNext = false;
            if (node.type === "activity") {
              const activityUserState = node.activityUserState;
              const hasBeenSubmitted = !("lastSyncedAt" in activityUserState) || activityUserState?.state?.submit_button?.inactive?.value === 2;

              shouldHideNext = !hasBeenSubmitted && (!!node.placement_uid || !!isGated);
            } else if (node.type === "section" && isGated) { // SHOULD ADD LEVEL TEST????
              shouldHideNext = !node.completed_section;
            }

            if (isGated && shouldHideNext) {
              const onClickActions = [];
              const remainingNodes = array.slice(currentIndex+1);
              const nextNode = remainingNodes.find((n) => ["section", "activity"].includes(n.type));
              if (nextNode) {
                const nextNodeAction = nextNode.type === "activity" ?
                  newSetNodeKVPairAction("root", "collapsed", false, "activity", nextNode.id) :
                  newSetNodeKVPairAction(nextNode.uid, "collapsed", false, "document");

                  onClickActions.push(nextNodeAction);
              } else {
                const activityScores = currentActivities.map((activity) => {
                  const activityUserState = activityUserStatesByActivityId[activity.id];
                  return "lastSyncedAt" in activityUserState && activityUserState.grade_percentage;
                }).filter((gradePercentage) => typeof gradePercentage === "number");

                if (activityScores.length > 0) {
                  const grade_percentage = R.sum(activityScores) / activityScores.length;
                  onClickActions.push(
                    newSetNodeKVPairAction(rootNode.uid, "grade_percentage", grade_percentage, "document"),
                  );
                }

                onClickActions.push(
                  newSetNodeKVPairAction(rootNode.uid, "completed_document", true, "document"),
                );
              }

              if (node.type === "activity") {
                const finalButton = node.content.flat().map((section) => section.content).flat().reverse().find((child) => child.type === "button");

                if (finalButton) {
                  if (!nextNode) {
                    finalButton.title = finalButton.title == "Submit" ? "Submit and Complete Case" : finalButton.title;
                  }

                  finalButton.onClick = (finalButton.onClick || []).concat(onClickActions);
                }
              } else if (node.type === "section") { // SHOULD ADD LEVEL TEST????
                onClickActions.push(
                  newSetNodeKVPairAction(node.uid, "completed_section", true),
                  newSetNodeKVPairAction(node.uid, "collapsed", true),
                );

                node.content.push(
                  generateNode("button", {
                    uid: `${node.uid}_next_button`,
                    title: nextNode ? "Continue" : "Complete Case",
                    onClick: onClickActions,
                  }),
                );
              }
            }

            return {
              shouldHide: shouldHideNext,
              newContent: [...newContent, node],
            };
          }
        }, {shouldHide: false, newContent: []});
        rootNode.content = reduceResult.newContent;

        if ("documentSetDocumentState" in this.props && this.props.documentSetDocumentState && "lastSyncedAt" in this.props.documentSetDocumentState && rootNode.completed_document) {
          const newContent = [
            generateNode("completionNotice", {
              uid: `${rootNode.uid}_complete_paragraph`,
              grade_percentage: this.props.documentSetDocumentState?.grade_percentage,
              attempt_index: this.props.documentSetDocumentState?.attempt_index,
              nextDocumentSetDocumentRelationId: this.props.nextDocumentSetDocumentRelationId,
            }),
          ];

          const lastSectionIndex = R.findLastIndex((n) => n.type === "section" || n.type === "activity", rootNode.content);
          rootNode.content = R.insertAll(lastSectionIndex + 1, newContent, rootNode.content);
        }
      }

      if (isGated) {
        let progress = 0;
        if (rootNode.completed_document) {
          progress = 100.0;
        } else {
          const progressSections = rootNode.content.filter((child) => "section" === child.type);
          const completedSections = progressSections.filter((section) => {
            const hasNextClicked = section.completed_section;

            return hasNextClicked;
          });

          const completedActivities = currentActivities.filter((activity) => {
            const activityUserState = activityUserStatesByActivityId[activity.id];
            const hasBeenSubmitted = "lastSyncedAt" in activityUserState && activityUserState?.state?.submit_button?.inactive?.value === 2;

            return hasBeenSubmitted;
          });

          const allPossibleCount = progressSections.length + currentActivities.length;
          const allCompletedCount = completedSections.length + completedActivities.length;
          progress = (allCompletedCount * 100.0) / allPossibleCount;
        }

        // TODO: ADD BACK IN
        // progressBar = (
        //   <div className="fixed bottom-0 right-20 z-40 p-3 bg-white bg-opacity-75 backdrop-blur-3xl border border-green-cyan shadow-lg">
        //     <h3>Progress: {Math.floor(progress)}%</h3>
        //   </div>
        // )
      }

      if ("documentSetDocumentState" in this.props && this.props.documentSetDocumentState && "lastSyncedAt" in this.props.documentSetDocumentState && this.props.documentSetDocumentState?.reviewed_at) {
        caseReview = (
          <div className="activity-review">
            <h4>Review:</h4>
            <div className="activity-review-inside">
              <p>{this.props.documentSetDocumentState.review_comment}</p>
            </div>
          </div>
        );
      }
    }

    if (!isEditDisplayMode) {
      rootNode = addCloseDrawersButtonToContent(rootNode);
    }

    return (
      <div className="doc-container">
        {editState.isEditor && (
          <DocTopBar
            currentDocument={currentDocument}
            currentVersion={currentVersion}
            currentUser={currentUser}
            currentRoleDefinition={currentRoleDefinitionCompiled}
            displayState={displayState}
            editState={editState}
          />
        )}
        <div className={classNames("doc-body", "normal-display-mode", {"edit-bar-bump": editState.isEditor})}>
          <DocSection
            isRootSection

            {...rootNode}
            currentDocument={currentDocument}
            currentVersion={currentVersion}
            currentUser={currentUser}
            currentRoleDefinition={currentRoleDefinitionCompiled}
            currentGroup={currentGroup}
            currentDocumentSetDocumentRelation={currentDocumentSetDocumentRelation}
            imagingTypes={imagingTypes}
            displayState={displayState}
            editState={editState}
            aboveContent={caseReview}
            activityActions={activityActions}
            isGated={isGated}
          />
        </div>
        {editState.isEditor && (
          <DocMediaModal
            currentDocument={currentDocument}
            currentVersion={currentVersion}
            currentUser={currentUser}
            currentRoleDefinition={currentRoleDefinitionCompiled}
            editState={editState}
            displayState={displayState}
          />
        )}
        {editState.isEditor && (
          <DocEditMetadataModal
            currentDocument={currentDocument}
            currentVersion={currentVersion}
            currentUser={currentUser}
            currentRoleDefinition={currentRoleDefinitionCompiled}
            editState={editState}
            rootNode={rootNode}
          />
        )}
        <DocModals
          currentDocument={currentDocument}
          currentVersion={currentVersion}
          currentUser={currentUser}
          currentRoleDefinition={currentRoleDefinitionCompiled}
          currentDocumentSetDocumentRelation={currentDocumentSetDocumentRelation}
          displayState={displayState}
          possibleActivities={possibleActivities}
        />
        {progressBar}
      </div>
    );
  }
}

export default (props: OwnProps) => {
  const ConnectedDocContainer = ReactRedux.connect(mapStateToProps, mapDispatchToProps, mergeProps)(DocContainer as any);

  return (
    <ReactRedux.Provider store={store()}>
      <ConnectedDocContainer {...props} />
    </ReactRedux.Provider>
  );
};
